import AsyncStorage from "@react-native-async-storage/async-storage";
import { t } from "@/localization";

// A nonprofit arriving from eventlive.pro/nonprofit, e.g.
// /signin?account_type=business&broadcaster_role=nonprofit&company_name=…
//   &email=…&pledge_org_id=<uuid>&nonprofit_lead=…&next=fundraiser&utm_…
// or an existing customer from an email (…&next=fundraiser&account=existing),
// who is sent to sign in rather than through new-account onboarding.
// The intent survives signup, social sign-in, onboarding steps and reloads,
// and is cleared once the fundraiser is saved or the banner is dismissed.
export const FUNDRAISER_INTENT_KEY = "pendingFundraiserIntent";
export const FUNDRAISER_INTENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const VERSION = 1;
// Answers OnboardingNew keeps between steps (see OnboardingNew.js).
const ONBOARDING_DATA_KEY = "onboardingData";

// Landing-page params worth keeping in signup metadata when the URL is lost
// (e.g. /onboarding → /signin navigation drops the query string).
const SIGNUP_METADATA_KEYS = [
  "account_type",
  "broadcaster_role",
  "company_name",
  "company_url",
  "pledge_org_id",
  "pledge_ein",
  "nonprofit_lead",
  "next",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

let cachedIntent; // undefined = not read yet, null = none

const clean = (value, max = 200) => {
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === "string" ? first.trim().slice(0, max) : "";
};

export const normalizeEin = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits.length === 9 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : "";
};

const isPledgeId = (value) => /^[A-Za-z0-9_-]{1,80}$/.test(value);

// Websites from links end up as the logo link on event pages, so keep only
// http(s) URLs with a real host; bare domains get https://.
export const safeWebsite = (value) => {
  const text = clean(value, 200);
  if (!text) return "";
  const url = /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
  return /^https?:\/\/[^\s/?#@]+\.[^\s/?#@]+([/?#]\S*)?$/i.test(url) ? url : "";
};
const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const parseFundraiserIntent = (params, now = Date.now()) => {
  if (!params || typeof params !== "object") return null;
  const get = (key, max) => clean(params[key], max);

  const next = get("next", 40).toLowerCase();
  const pledgeOrgId = isPledgeId(get("pledge_org_id", 80))
    ? get("pledge_org_id", 80)
    : "";
  const pledgeEin = normalizeEin(get("pledge_ein", 20));
  if (next !== "fundraiser" && !pledgeOrgId && !pledgeEin) return null;

  const metadata = {};
  for (const key of SIGNUP_METADATA_KEYS) {
    const value =
      key === "company_url" ? safeWebsite(get(key, 200)) : get(key, 200);
    if (value) metadata[key] = value;
  }
  delete metadata.pledge_org_id;
  delete metadata.pledge_ein;
  if (pledgeOrgId) metadata.pledge_org_id = pledgeOrgId;
  if (pledgeEin) metadata.pledge_ein = pledgeEin;

  const accountType = get("account_type", 20);
  const role = get("broadcaster_role", 40);
  const email = get("email", 254);
  const nonprofitLead = get("nonprofit_lead", 80);

  return {
    version: VERSION,
    createdAt: now,
    autoStart: next === "fundraiser",
    pledgeOrgId,
    pledgeEin,
    orgName: get("company_name", 120),
    orgUrl: safeWebsite(get("company_url", 200)),
    accountType: ["business", "individual"].includes(accountType)
      ? accountType
      : "",
    broadcasterRole: /^[a-z_]{1,40}$/.test(role) ? role : "",
    email: isEmail(email) ? email : "",
    // An existing customer (email links): signed-out visitors go straight to
    // sign in instead of the new-account welcome (OnboardingNew).
    existingAccount: get("account", 20).toLowerCase() === "existing",
    // eventlive.pro/nonprofit lead, claimed for the streaming-fee waiver once
    // signed in (nonprofitWaiver.js); waiverClaim holds evt.live's answer.
    nonprofitLead: isPledgeId(nonprofitLead) ? nonprofitLead : "",
    waiverClaim: null,
    metadata,
    // captured → event_created (slug + boundAt set) → cleared once the
    // fundraiser is saved or the banner is dismissed
    stage: "captured",
    slug: "",
    boundAt: 0,
    prompted: false,
  };
};

const isValidIntent = (intent, now = Date.now()) =>
  !!intent &&
  typeof intent === "object" &&
  intent.version === VERSION &&
  Number.isFinite(intent.createdAt) &&
  now - intent.createdAt <= FUNDRAISER_INTENT_MAX_AGE_MS;

const persist = async (intent) => {
  cachedIntent = intent || null;
  try {
    if (intent) {
      await AsyncStorage.setItem(FUNDRAISER_INTENT_KEY, JSON.stringify(intent));
    } else {
      await AsyncStorage.removeItem(FUNDRAISER_INTENT_KEY);
    }
  } catch (error) {
    // Storage can be unavailable (private mode); the in-memory copy still
    // carries the intent through this session.
  }
  return cachedIntent;
};

// Synchronous read of what this session already knows (no storage access).
export const peekFundraiserIntent = () =>
  isValidIntent(cachedIntent) ? cachedIntent : null;

export const readFundraiserIntent = async () => {
  if (cachedIntent !== undefined) {
    if (isValidIntent(cachedIntent)) return cachedIntent;
    if (cachedIntent) await persist(null);
    return null;
  }
  let raw = null;
  try {
    raw = await AsyncStorage.getItem(FUNDRAISER_INTENT_KEY);
  } catch (error) {
    raw = null;
  }
  let parsed = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch (error) {
    parsed = null;
  }
  // Another read or capture may have finished while storage was loading.
  if (cachedIntent !== undefined) return peekFundraiserIntent();
  if (isValidIntent(parsed)) {
    cachedIntent = parsed;
    return parsed;
  }
  cachedIntent = null;
  if (raw) await persist(null);
  return null;
};

const sameOrganization = (a, b) =>
  !!a &&
  !!b &&
  (a.pledgeOrgId || "") === (b.pledgeOrgId || "") &&
  (a.pledgeEin || "") === (b.pledgeEin || "") &&
  (a.orgName || "") === (b.orgName || "");

// `freshVisit`: the app was just opened from the landing page link (web).
export const captureFundraiserIntent = async (
  params,
  { freshVisit = false } = {}
) => {
  const parsed = parseFundraiserIntent(params);
  if (!parsed) return null;
  const existing = await readFundraiserIntent();
  // Returning with the same organization (reload, back button, a second click)
  // keeps an event that was already created for it.
  if (existing && sameOrganization(existing, parsed)) {
    return persist({
      ...parsed,
      stage: existing.stage,
      slug: existing.slug,
      boundAt: existing.boundAt,
      waiverClaim:
        nonprofitLeadOf(existing) === parsed.nonprofitLead
          ? existing.waiverClaim || null
          : null,
    });
  }
  // A new landing-page visit starts onboarding afresh: answers left over from
  // an abandoned onboarding on this device (e.g. a wedding title and link)
  // must not end up in this nonprofit's signup and create a stray event.
  if (freshVisit) {
    try {
      await AsyncStorage.removeItem(ONBOARDING_DATA_KEY);
    } catch (error) {
      // Nothing stored or storage unavailable.
    }
  }
  return persist(parsed);
};

export const captureFundraiserIntentFromUrl = (search) => {
  try {
    const params = Object.fromEntries(new URLSearchParams(search || ""));
    if (!parseFundraiserIntent(params)) return null;
    return captureFundraiserIntent(params, { freshVisit: true });
  } catch (error) {
    return null;
  }
};

export const updateFundraiserIntent = async (patch) => {
  const intent = await readFundraiserIntent();
  if (!intent) return null;
  return persist({ ...intent, ...patch });
};

export const clearFundraiserIntent = async () => {
  await persist(null);
};

// The lead to claim; intents saved before nonprofitLead existed keep it in
// their signup metadata.
export const nonprofitLeadOf = (intent) => {
  const lead = intent?.nonprofitLead || intent?.metadata?.nonprofit_lead;
  return typeof lead === "string" && isPledgeId(lead) ? lead : "";
};

// The suggested title of the event created for the fundraiser, in the
// broadcaster's language (guests see it; they can change it).
export const fundraiserEventTitle = (intent) => {
  const name = (intent?.orgName || "").trim();
  return name ? t("{name} Live Fundraiser", { name }).slice(0, 120) : "";
};

export const fundraiserSignupMetadata = (intent) =>
  intent?.metadata && typeof intent.metadata === "object"
    ? { ...intent.metadata }
    : {};

const withoutEmptyValues = (values) =>
  Object.fromEntries(
    Object.entries(values || {}).filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
  );

// Signup metadata = landing params kept in the intent < params in the current
// URL < onboarding answers. With an intent, onboarding fields left empty never
// wipe what the landing page provided (company name, website, role). Without
// one this is exactly the previous `{ ...metadata, ...onboarding }` merge.
// A nonprofit lead's first event is never created by the signup itself: it
// would be created locked, before the waiver is claimed (see EventList).
export const buildSignupMetadata = ({ metadata, intent, onboarding } = {}) => {
  const base = { ...fundraiserSignupMetadata(intent), ...(metadata || {}) };
  const afterClaim = nonprofitLeadOf(intent) ? { skipEventCreation: true } : {};
  if (!onboarding) return { ...base, ...afterClaim };
  return {
    ...base,
    ...(intent ? withoutEmptyValues(onboarding) : onboarding),
    onboarding_done: true,
    ...afterClaim,
  };
};

// Prefill for the fundraising screen: explicit route params
// (/event/:slug/fundraising?pledge_org_id=…) win over a pending intent that was
// created for this event.
export const fundraiserPrefill = ({ params, intent, slug } = {}) => {
  const id = clean(params?.pledge_org_id, 80);
  const ein = normalizeEin(clean(params?.pledge_ein, 20));
  if ((id && isPledgeId(id)) || ein) {
    return {
      id: isPledgeId(id) ? id : "",
      ein,
      name: clean(params?.org_name, 120),
      fromIntent: false,
    };
  }
  if (
    intent &&
    slug &&
    intent.slug === slug &&
    (intent.pledgeOrgId || intent.pledgeEin || intent.orgName)
  ) {
    return {
      id: intent.pledgeOrgId || "",
      ein: intent.pledgeEin || "",
      name: intent.orgName || "",
      fromIntent: true,
    };
  }
  return null;
};

// Test helper: forget the in-memory copy so the next read hits storage.
export const __resetFundraiserIntentCache = () => {
  cachedIntent = undefined;
};
